import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Shield } from 'lucide-react';

const Privacy = ({ onBack }) => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 py-8 sm:py-12">
      <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
        {/* Back Button */}
        <Button 
          variant="outline" 
          onClick={onBack}
          className="mb-4 sm:mb-6 border-emerald-600 text-emerald-600 hover:bg-emerald-50 text-sm sm:text-base"
        >
          ← {t('legal.backToHome', 'Zurück zur Startseite')}
        </Button>

        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <Shield className="h-12 w-12 sm:h-16 sm:w-16 text-emerald-600 mx-auto mb-3 sm:mb-4" />
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            {t('privacy.title', 'Datenschutzerklärung')}
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            {t('privacy.lastUpdated', 'Stand: Dezember 2025')}
          </p>
        </div>

        {/* Content */}
        <Card className="shadow-lg">
          <CardContent className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6">
            {/* Section 1: General */}
            <section>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('privacy.section1.title', '1. Allgemeine Hinweise')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                {t('privacy.section1.content', 'Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.')}
              </p>
            </section>

            {/* Section 2: Data Collection */}
            <section>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('privacy.section2.title', '2. Datenerfassung auf dieser Website')}
              </h2>
              <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-800 mb-2">
                {t('privacy.section2.subtitle', 'Wer ist verantwortlich für die Datenerfassung?')}
              </h3>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                {t('privacy.section2.content1', 'Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können Sie dem Impressum dieser Website entnehmen.')}
              </p>
              <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-800 mb-2">
                {t('privacy.section2.subtitle2', 'Wie erfassen wir Ihre Daten?')}
              </h3>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                {t('privacy.section2.content2', 'Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen (z.B. durch Eingabe in ein Kontaktformular). Andere Daten werden automatisch beim Besuch der Website durch unsere IT-Systeme erfasst (z.B. Browsertyp, Betriebssystem).')}
              </p>
            </section>

            {/* Section 3: What We Collect */}
            <section>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('privacy.section3.title', '3. Welche Daten erheben wir?')}
              </h2>
              <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-2">
                <li>{t('privacy.section3.item1', 'Name, E-Mail-Adresse, Telefonnummer (bei Buchungen)')}</li>
                <li>{t('privacy.section3.item2', 'Adresse (für Dienstleistungen)')}</li>
                <li>{t('privacy.section3.item3', 'IP-Adresse und Browserinformationen')}</li>
                <li>{t('privacy.section3.item4', 'Datum und Uhrzeit des Zugriffs')}</li>
              </ul>
            </section>

            {/* Section 4: How We Use Data */}
            <section>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('privacy.section4.title', '4. Wofür nutzen wir Ihre Daten?')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                {t('privacy.section4.content', 'Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden.')}
              </p>
              <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-2">
                <li>{t('privacy.section4.purpose1', 'Abwicklung von Buchungen und Zahlungen')}</li>
                <li>{t('privacy.section4.purpose2', 'Kommunikation mit Kunden')}</li>
                <li>{t('privacy.section4.purpose3', 'Verbesserung unserer Dienstleistungen')}</li>
                <li>{t('privacy.section4.purpose4', 'Erfüllung gesetzlicher Verpflichtungen')}</li>
              </ul>
            </section>

            {/* Section 5: Stripe */}
            <section>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('privacy.section5.title', '5. Zahlungsanbieter (Stripe)')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                {t('privacy.section5.content', 'Wir nutzen Stripe für die Zahlungsabwicklung. Bei Zahlungen über Stripe werden Ihre Zahlungsdaten direkt an Stripe übermittelt. Wir speichern keine vollständigen Kreditkartendaten. Weitere Informationen finden Sie in der Datenschutzerklärung von Stripe: https://stripe.com/privacy')}
              </p>
            </section>

            {/* Section 6: Your Rights */}
            <section>
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('privacy.section6.title', '6. Ihre Rechte')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                {t('privacy.section6.intro', 'Sie haben jederzeit das Recht:')}
              </p>
              <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-2">
                <li>{t('privacy.section6.right1', 'Auskunft über Ihre gespeicherten Daten zu erhalten')}</li>
                <li>{t('privacy.section6.right2', 'Berichtigung unrichtiger Daten zu verlangen')}</li>
                <li>{t('privacy.section6.right3', 'Löschung Ihrer Daten zu verlangen')}</li>
                <li>{t('privacy.section6.right4', 'Einschränkung der Datenverarbeitung zu verlangen')}</li>
                <li>{t('privacy.section6.right5', 'Widerspruch gegen die Verarbeitung einzulegen')}</li>
              </ul>
            </section>

            {/* Section 7: Contact */}
            <section className="bg-emerald-50 p-4 sm:p-6 rounded-lg">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('privacy.section7.title', '7. Kontakt')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                {t('privacy.section7.content', 'Bei Fragen zur Erhebung, Verarbeitung oder Nutzung Ihrer personenbezogenen Daten, bei Auskünften, Berichtigung, Sperrung oder Löschung von Daten wenden Sie sich bitte an:')}
              </p>
              <div className="mt-4 text-sm sm:text-base text-gray-700">
                <p className="font-semibold">Brillanz & Glanz</p>
                <p>Schweiz</p>
                <p className="mt-2 break-all">E-Mail: thaqi.kujtim@gmx.net</p>
                <p>Telefon: +41 76 766 94 30</p>
                <p className="mt-2 text-xs sm:text-sm text-gray-500 italic">Handelsregister-Eintragung ausstehend</p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Privacy;